import React from 'react';
import PropTypes from 'prop-types';

import momentWithAustinTimezone from 'common/util/momentWithAustinTimezone';

import SrText from 'client/components/SrText';
import SrFlex from 'client/components/SrFlex';
import SrBox from 'client/components/SrBox';
import SrSetupHealthGoLiveSectionItem from 'client/components/SrSetupHealthGoLiveSection/SrSetupHealthGoLiveSectionItem/SrSetupHealthGoLiveSectionItem';

const SrSetupHealthGoLiveSection = ({
  photographyRequired,
  photographyArrivedDate,
  photographyLiveDate,
  photographyLiveCompletedDate,
  listingFirstInReviewDate,
  listingFirstActiveDate,
}) => {

  const GO_LIVE_ITEMS = [
    {
      title: 'Photo Hand-off',
      key:'goLiveItem_photoHandOff',
      startDate: photographyArrivedDate,
      endDate: photographyLiveDate,
      startLabel: 'Arrived at property',
      endLabel: 'Photos uploaded',
      hourTreshold: 8,
      stepDescription: 'Time between the photographer arriving at the property,' +
        ' to uploading photos on Sunroom.',
      maxWidth: '85px',
    },
    {
      title: 'QA Photos',
      key:'goLiveItem_qaPhotos',
      startDate: photographyLiveDate,
      endDate: photographyLiveCompletedDate,
      startLabel: 'Photos uploaded',
      endLabel: 'Reviewed and sorted',
      hourTreshold: 1,
      stepDescription: 'Time between uploading photos on Sunroom to reviewing and sorting the photos.',
      maxWidth: '95px',
    },
    {
      title: 'AM Activate',
      key:'goLiveItem_amActivate',
      startDate: listingFirstInReviewDate,
      endDate: listingFirstActiveDate,
      startLabel: 'Reviewed',
      endLabel: 'Listing active',
      hourTreshold: 1,
      stepDescription: 'Time between sending to AM for review, to the AM making the listing active.',
      maxWidth: '125px',
    },
  ];

  const _getIsGoLiveItemOverDue = ({
    startDate,
    endDate,
    hourTreshold,
  }) => {
    if (
      !startDate
      || typeof startDate !== 'string'
      || typeof hourTreshold !== 'number'
      || (endDate && typeof endDate !== 'string')
    ) {
      return false;
    }

    const endDateMoment = endDate
      ? momentWithAustinTimezone(endDate)
      : momentWithAustinTimezone();

    return endDateMoment.diff(momentWithAustinTimezone(startDate), 'hours') >= hourTreshold;
  };

  const _getActiveComponentKey = () => {
    let activeComponentKey = null;

    for (const { key, startDate } of GO_LIVE_ITEMS) {
      if (!!startDate || activeComponentKey === null) {
        activeComponentKey = key;
      }
    }
    return activeComponentKey;
  };

  const _buildGoLiveItemComponents = () => {
    const goLiveItemComponents = [];
    const activeComponentKey = _getActiveComponentKey();

    for (const item of GO_LIVE_ITEMS) {
      const isOverdue = _getIsGoLiveItemOverDue({
        startDate: item.startDate,
        endDate: item.endDate,
        hourTreshold: item.hourTreshold,
      });

      goLiveItemComponents.push(
        <SrSetupHealthGoLiveSectionItem
          title={item.title}
          key={item.key}
          stepDescription={item.stepDescription}
          isOverdue={isOverdue}
          maxWidth={item.maxWidth}
          isActive={item.key === activeComponentKey}
          startDate={item.startDate}
          endDate={item.endDate}
          startLabel={item.startLabel}
          endLabel={item.endLabel}
        />
      );
    }
    return goLiveItemComponents;
  };

  const _getGoLiveItemComponents = () => {
    if (!photographyRequired) {
      return (
        <SrBox
          marginTop='7px'
          marginLeft='8px'
        >
          <SrText
            font='greycliff'
            fontSize='16px'
            color='grey'
            weight='600'
          >
            Photography job is not requested.
          </SrText>
        </SrBox>
      );
    }
    const itemComponents = _buildGoLiveItemComponents();
    return (
      <SrFlex
        marginTop='7px'
        padding='8px 9px'
        border='0.6px solid rgba(151, 151, 151, 0.5)'
        borderRadius='8px'
        justify='around'
      >
        {itemComponents}
      </SrFlex>
    );
  };

  const goLiveItemComponents = _getGoLiveItemComponents();

  return (
    <SrBox>
      <SrText
        font='greycliff'
        fontSize='18px'
        weight='800'
      >
        Go Live
      </SrText>
      {goLiveItemComponents}
    </SrBox>
  );
};

SrSetupHealthGoLiveSection.propTypes = {
  photographyRequired: PropTypes.bool.isRequired,
  photographyArrivedDate: PropTypes.string,
  photographyLiveDate: PropTypes.string,
  photographyLiveCompletedDate: PropTypes.string,
  listingFirstInReviewDate: PropTypes.string,
  listingFirstActiveDate: PropTypes.string,
};

export default SrSetupHealthGoLiveSection;
